/**
 * Deprecations for brandy/shape-divider.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * v1 baked the gradient stops into the saved SVG as `stop-color` attributes,
 * defaulting to `#0061FE` → `#00C2FF`. v2 leaves both attributes empty by
 * default and lets the stylesheet resolve them to the theme's primary colour,
 * so the stops are written as inline `style` instead.
 *
 * This block saves static markup, so the change is not cosmetic to Gutenberg:
 * without the entry below, every divider saved before v2 parses with the new
 * empty defaults, fails the markup comparison, and the editor offers to
 * "recover" a block that was never broken.
 *
 * The entry restores the v1 attribute defaults, so old content keeps rendering
 * its explicit blue — the colours an author already approved do not silently
 * change under them. Only newly inserted dividers pick up the theme colour.
 * No `migrate` is needed: the attributes the deprecation parses are already
 * valid input for the current save.
 *
 * RULE: never edit a shipped deprecation; add new entries to the FRONT of the
 * array as the block evolves.
 */

import { useBlockProps } from '@wordpress/block-editor';
import { getShapeLayers, getShapeTop } from './shapes';
import { getWrapperClasses, getWrapperStyles } from './styles';

// Attributes as declared in the v1 block.json — the defaults are the point.
const v1Attributes = {
  shape: { type: 'string', default: 'waves' },
  height: { type: 'number', default: 110 },
  flipX: { type: 'boolean', default: false },
  invert: { type: 'boolean', default: false },
  fillType: { type: 'string', default: 'gradient' },
  fillColor: { type: 'string', default: '' },
  gradientFrom: { type: 'string', default: '#0061FE' },
  gradientTo: { type: 'string', default: '#00C2FF' },
  gradientAngle: { type: 'number', default: 90 },
  uniqueId: { type: 'string', default: '' },
};

// A copy of the v1 SVG, frozen. It must not import shape-svg.jsx: that file
// moves on with the block, and a deprecation whose markup drifts stops matching
// the content it exists to rescue.
function V1Svg({ attributes }) {
  const { shape, fillType, gradientFrom, gradientTo, gradientAngle, uniqueId } =
    attributes;

  const isGradient = fillType === 'gradient';
  const gradientId = `brandy-shape-${uniqueId || 'default'}`;
  const fill = isGradient ? `url(#${gradientId})` : undefined;
  const top = getShapeTop(shape);

  return (
    <svg
      className="brandy-shape-divider__svg"
      viewBox={`0 ${top} 1200 ${120 - top}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {isGradient && (
        <defs>
          <linearGradient
            id={gradientId}
            gradientTransform={`rotate(${gradientAngle}, 0.5, 0.5)`}
          >
            <stop offset="0%" stopColor={gradientFrom} />
            <stop offset="100%" stopColor={gradientTo} />
          </linearGradient>
        </defs>
      )}

      {getShapeLayers(shape).map((layer, index) => (
        <path
          key={index}
          d={layer.d}
          fill={fill}
          fillOpacity={layer.opacity === 1 ? undefined : layer.opacity}
        />
      ))}
    </svg>
  );
}

const v1 = {
  attributes: v1Attributes,
  save({ attributes }) {
    const blockProps = useBlockProps.save({
      className: getWrapperClasses(attributes),
      style: getWrapperStyles(attributes),
    });

    return (
      <div {...blockProps}>
        <V1Svg attributes={attributes} />
      </div>
    );
  },
};

export default [v1];
